import type { SpecUsageSummary } from '@parcel/types';
import {
  Badge,
  Button,
  Progress,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  buttonVariants,
  cn,
} from '@parcel/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { Coins, HardHat, Info, type LucideIcon, Users } from 'lucide-react';
import * as React from 'react';
import {
  specAllowanceBadge,
  specAllowanceNote,
  specAllowanceView,
} from '../../lib/spec-allowance.js';
import {
  SpecBillingError,
  type SpecQueryRows,
  type SpecWebCalls,
  getEnrichmentCredits,
  getSpecBilling,
  getSpecQueryRows,
  getSpecUsageSummary,
  getSpecWebCalls,
  specBillingSaveMessage,
} from '../../lib/spec-billing.js';
import { isSpecSurfaceVisible } from '../../lib/spec-config.js';
import { useSpecLaunchState } from '../../lib/spec-launch.js';
import { formatSpecBalanceUsd } from '../../lib/spec-usage-guard.js';
import {
  type WorkspaceMembersResponse,
  type WorkspaceSummary,
  getWorkspace,
  getWorkspaceSlugFromPath,
  listWorkspaceMembers,
} from '../../lib/workspaces.js';
import { getWorkspaceScopedPath } from '../app-sidebar-nav.js';
import { ManageSeatsDialog } from '../billing/manage-seats-dialog.js';
import { BuyCreditsDialog } from './billing-credits-section.js';
import { EnrichmentCreditsPanel } from './enrichment-credits-panel.js';
import { SettingsPageHeader, SettingsPanel, SettingsShell } from './settings-shell.js';
import { seatPhrase } from './usage-settings.js';

/**
 * Workspace > Usage: what this workspace has consumed this period.
 *
 * Adding seats and buying credits open the same `ManageSeatsDialog` and
 * `BuyCreditsDialog` that Billing uses, in place; one dialog per write, no second
 * write path. Everything else links to the page that owns it.
 *
 * The allowance and the balance are the two things a workspace can run out of,
 * and they are never summed. An allowance is chat metered against a limit that
 * renews and expires with the period; a balance is money that survives the
 * boundary and funds everything else, plus the chat that overflows. One number
 * for both would have to answer for both.
 *
 * Gated PER SECTION, not wholesale. Members are not a Spec concept, so that
 * card renders even while Spec is dark; the allowance, the balance, and the AI
 * seat card sit inside `SpecUsageSection`, which mirrors
 * the guard/content split at `spec-billing-card.tsx:69`.
 */

const BILLING_FAILURE_MESSAGE =
  'Spec AI usage is unavailable right now. Try again in a few minutes.';

/**
 * The definition behind a stat, one click away rather than under it.
 *
 * These sentences explain what a figure counts, which is worth having and is
 * not worth three lines of every card -- the descriptions were taller than the
 * numbers they described, and a row of stats you have to read is not a row of
 * stats. On demand, they cost nothing.
 *
 * `TooltipContent` in `@parcel/ui` already renders on `bg-popover`, not
 * shadcn's inverted `bg-primary`, so it follows the theme in light and dark
 * the way every other floating surface here does.
 */
function UsageStatHint({ label, hint }: { label: string; hint: string }) {
  return (
    <Tooltip>
      <TooltipTrigger
        type="button"
        // The accessible name is what the "no write controls" test enumerates:
        // an info trigger is allowed on this page, anything else is not.
        aria-label={`About ${label}`}
        className="rounded-full text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <Info className="size-3.5" aria-hidden />
      </TooltipTrigger>
      <TooltipContent className="max-w-64">{hint}</TooltipContent>
    </Tooltip>
  );
}

/**
 * One stat in the row under the hero: what it counts, the figure, and the two
 * things you can do about it.
 *
 * Two actions, deliberately at different weights. The outlined one at top right
 * is the decision the card exists to prompt ("Add members", "Buy credits"); the
 * quiet "See details" underneath is for reading rather than doing. A card with
 * one undifferentiated link made every stat look equally like a call to action.
 *
 * The prompt opens the shared dialog when it has `onClick`, and links when it has `to`.
 */
/** A card's top-right prompt: a dialog it opens in place, or a page it links to. */
type StatAction = { label: string; onClick: () => void } | { label: string; to: string };

function StatActionControl({
  action,
  className,
  testId,
}: {
  action: StatAction;
  className?: string;
  testId?: string;
}) {
  if ('onClick' in action) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={className}
        onClick={action.onClick}
        data-testid={testId}
      >
        {action.label}
      </Button>
    );
  }
  return (
    <Link
      to={action.to}
      className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), className)}
      data-testid={testId}
    >
      {action.label}
    </Link>
  );
}

function UsageStatCard({
  label,
  value,
  hint,
  icon: Icon,
  action,
  detailsHref,
  testId,
}: {
  label: string;
  value: string;
  /** Rendered in the label's info tooltip, never inline. */
  hint: string;
  /**
   * The mark beside the label. Typed as the component rather than as a node so
   * the card owns the size and the color, and no two icons in the row can
   * arrive at different sizes.
   */
  icon: LucideIcon;
  /** The prompt at top right: where this card's decision gets made. */
  action: StatAction;
  /** The quiet link underneath: where this card's figure gets explained. */
  detailsHref: string;
  testId?: string;
}) {
  return (
    <div className="flex flex-col rounded-lg border p-4" data-testid={testId}>
      {/*
        Wraps rather than truncates. The column width is what makes these fit
        (see `settings-shell.tsx`), but a label is the one thing on this card
        that must never be clipped -- a stat you cannot name is not a stat -- so
        the button drops to its own line before that can happen at any width.
      */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Icon className="size-4 shrink-0 text-brand" aria-hidden />
          {label}
          <UsageStatHint label={label} hint={hint} />
        </p>
        <StatActionControl action={action} className="shrink-0" />
      </div>
      <p className="mt-3 text-2xl font-semibold tabular-nums">{value}</p>
      {/*
        `mt-auto` rather than `justify-between` on the card: the label row can
        wrap to two lines on a narrow column, and only pushing this to the
        bottom keeps all three "See details" links on one line across the row.
      */}
      <Link
        to={detailsHref}
        className="mt-auto pt-3 text-sm font-medium text-brand hover:underline"
      >
        See details
      </Link>
    </div>
  );
}

/**
 * The same box, before its numbers land.
 *
 * Deliberately built from the card's own class list rather than a generic
 * placeholder, so the row does not resize when the reads settle. A skeleton
 * whose shape differs from the content is just a second kind of pop.
 */
function UsageStatCardSkeleton() {
  return (
    <div className="flex flex-col rounded-lg border p-4">
      <div className="flex items-start justify-between gap-2">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-8 w-24" />
      </div>
      <Skeleton className="mt-3 h-8 w-16" />
      <div className="mt-auto pt-3">
        <Skeleton className="h-4 w-20" />
      </div>
    </div>
  );
}

/**
 * The card's box with an explanation instead of a figure.
 *
 * Distinct from the skeleton, which promises a number that is still coming.
 * This one is for a read that SETTLED with nothing, where a pulsing placeholder
 * would be a lie that never resolves.
 */
function UsageStatCardUnavailable({ label, icon: Icon }: { label: string; icon: LucideIcon }) {
  return (
    <div className="flex flex-col rounded-lg border p-4" data-testid="usage-stat-unavailable">
      <p className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
        <Icon className="size-4 shrink-0 text-brand" aria-hidden />
        {label}
      </p>
      <p className="mt-3 text-sm text-muted-foreground">
        Unavailable right now. Reload in a few minutes.
      </p>
    </div>
  );
}

/** The hero's own shape: label, figure, action, meter, sub-line. */
function SpecAllowanceHeroSkeleton() {
  return (
    <SettingsPanel className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="mt-2 h-7 w-28" />
        </div>
        <Skeleton className="h-9 w-28" />
      </div>
      <Skeleton className="mt-3 h-2 w-full rounded-full" />
      <Skeleton className="mt-3 h-4 w-48" />
    </SettingsPanel>
  );
}

/**
 * `SpecUsageSummary` (`GET /spec/usage`) is the server's own computed balance
 * -- `includedGranted`/`includedUsed`/`includedRemaining` -- not derived from
 * seats here. A seat-multiplication formula (`purchasedSeatQuantity *
 * includedCreditsPerSeatMicros`) is only correct for `ai_addon` eligibility:
 * `purchasedSeatQuantity` is `subscription?.seatQuantity ?? 0`
 * (spec-usage.ts:389), which is unconditionally 0 for `free_grant_only` and
 * `trial`, so that formula reads every Free workspace's real monthly grant as
 * zero. Reading the server's own numbers is correct for all three tiers.
 *
 * Shown as PROGRESS, not as a balance. An allowance cannot be bought, cashed
 * out, or carried across the period boundary, and rendering it as a credit
 * figure invites all three questions.
 */
function SpecAllowanceHero({
  summary,
  billingHref,
  onAddSeats,
}: {
  summary: SpecUsageSummary;
  billingHref: string;
  /** Opens the seats dialog in place; null when this member cannot buy seats. */
  onAddSeats: (() => void) | null;
}) {
  // The same view the chat popover reads, so the two surfaces cannot disagree.
  const view = specAllowanceView(summary, Date.now());
  const badge = specAllowanceBadge(view);
  const note = specAllowanceNote(view);
  const label =
    view.kind === 'trial' ? 'Add AI seats' : view.kind === 'free' ? 'Upgrade' : 'Go to billing';
  const action: StatAction =
    view.kind === 'trial' && onAddSeats
      ? { label, onClick: onAddSeats }
      : { label, to: billingHref };

  return (
    <SettingsPanel className="p-4" data-testid="allowance-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
            <HardHat className="size-4 shrink-0 text-brand" aria-hidden />
            {view.label}
          </p>
          {/* A share used, never a credit count: the raw grant would disclose per-seat pricing. */}
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <p className="text-2xl font-semibold tabular-nums">{view.percentUsed}%</p>
            {badge ? (
              <Badge
                variant={
                  view.state === 'limit_reached' || view.state === 'ended' ? 'amber' : 'gray'
                }
              >
                {badge}
              </Badge>
            ) : null}
          </div>
        </div>
        <StatActionControl action={action} testId="allowance-action" />
      </div>
      <Progress
        value={view.percentUsed}
        aria-label={view.label}
        className="mt-3"
        data-testid="allowance-meter"
      />
      {note ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {note}
          {view.state === 'limit_reached' && view.kind !== 'trial' && view.date
            ? ` · Resets ${view.date}`
            : null}
        </p>
      ) : null}
    </SettingsPanel>
  );
}

const COMPACT_NUMBER = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const RATE_LIMIT_GRID = 'grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_7rem] items-center gap-4';

/** One quota: what it limits, a meter for the window, and used over limit. */
function RateLimitRow({
  label,
  used,
  limit,
  testId,
}: {
  label: string;
  used: number;
  limit: number;
  testId: string;
}) {
  const share = limit > 0 ? (used / limit) * 100 : 0;
  const percent = used > 0 && share < 1 ? '<1%' : `${Math.min(100, Math.floor(share))}%`;
  return (
    <div data-testid={testId} className={cn(RATE_LIMIT_GRID, 'border-t py-3 text-sm')}>
      <span className="truncate">{label}</span>
      <div className="flex items-center gap-3">
        <Progress
          value={Math.min(100, share)}
          aria-label={label}
          className="h-1.5 flex-1"
          indicatorClassName={share >= 100 ? 'bg-destructive' : undefined}
        />
        <span className="w-10 shrink-0 text-right tabular-nums text-muted-foreground">
          {percent}
        </span>
      </div>
      <span className="text-right tabular-nums">
        {COMPACT_NUMBER.format(used)}
        <span className="text-muted-foreground"> / {COMPACT_NUMBER.format(limit)}</span>
      </span>
    </div>
  );
}

/** Spec's daily caps as a table; a cap that is off or failed to load is left out. */
function SpecRateLimits({
  queryRows,
  webCalls,
}: {
  queryRows: SpecQueryRows | null;
  webCalls: SpecWebCalls | null;
}) {
  const rows = [
    queryRows?.available
      ? { label: 'Parcel search', testId: 'spec-query-rows', ...queryRows }
      : null,
    webCalls?.available ? { label: 'Web search', testId: 'spec-web-calls', ...webCalls } : null,
  ].filter((row) => row !== null);
  if (rows.length === 0) return null;

  return (
    <section data-testid="rate-limits">
      <h3 className="font-medium">Rate limits</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Your rate limits and usage over the last {rows[0]?.windowHours} hours. Limits depend on your
        plan.
      </p>
      <div className="mt-3">
        <div className={cn(RATE_LIMIT_GRID, 'pb-2 text-xs text-muted-foreground')}>
          <span>Limit</span>
          <span>Usage</span>
          <span className="text-right">Used / limit</span>
        </div>
        {rows.map((row) => (
          <RateLimitRow
            key={row.testId}
            label={row.label}
            used={row.used}
            limit={row.allowance}
            testId={row.testId}
          />
        ))}
      </div>
    </section>
  );
}

/** Dev-only local override can show this section while the server launch state is
 * still `off`, and the read then 404s spec_not_launched. Mirrors
 * spec-billing-card.tsx: nothing truthful to show for an unlaunched Spec. Checked
 * against either read: both go through the same `loadRuntimeAndAccess` guard
 * server-side, so either can carry the code first depending on which settles. */
function isNotLaunched(err: unknown): boolean {
  return err instanceof SpecBillingError && err.code === 'spec_not_launched';
}

/**
 * The row when there is no Spec content to put beside the seats.
 *
 * Shared by the two paths that reach that state -- Spec dark on the client, and
 * the server answering `spec_not_launched` -- because they had already
 * diverged: one returned this row and the other returned null, dropping a card
 * neither of them owns. The `TooltipProvider` is why sharing it matters rather
 * than being tidy: the card carries an info tooltip, and Radix THROWS when one
 * renders outside a provider, so the duplicated version did not degrade -- it
 * took the whole page down.
 */
function SeatsOnlyRow({ seatsCard }: { seatsCard: React.ReactNode }) {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="grid gap-4 sm:grid-cols-3">{seatsCard}</div>
    </TooltipProvider>
  );
}

/**
 * The Spec-gated body. TWO independent `useQuery` reads: the usage summary
 * powers the allowance and balance cards, while billing powers the AI-seats
 * card. Either read can fail without hiding the other cards.
 */
function SpecUsageSectionContent({
  workspaceId,
  membersHref,
  billingHref,
  seatsCard,
  onManageSeats,
  canBuyCredits,
  writesPaused,
}: {
  workspaceId: string;
  membersHref: string;
  billingHref: string;
  seatsCard: React.ReactNode;
  onManageSeats: (() => void) | null;
  /** Owners and admins check out; everyone else is sent to Billing, which says to ask one. */
  canBuyCredits: boolean;
  writesPaused: boolean;
}) {
  const [buyOpen, setBuyOpen] = React.useState(false);
  const billingQuery = useQuery({
    queryKey: ['spec-billing', workspaceId],
    queryFn: () => getSpecBilling(workspaceId),
  });
  const usageQuery = useQuery({
    queryKey: ['spec-usage-summary', workspaceId],
    queryFn: () => getSpecUsageSummary(workspaceId),
  });
  const queryRowsQuery = useQuery({
    queryKey: ['spec-query-rows', workspaceId],
    queryFn: () => getSpecQueryRows(workspaceId),
  });
  const queryRows = queryRowsQuery.data ?? null;
  const webCallsQuery = useQuery({
    queryKey: ['spec-web-calls', workspaceId],
    queryFn: () => getSpecWebCalls(workspaceId),
  });
  const webCalls = webCallsQuery.data ?? null;
  const enrichmentQuery = useQuery({
    queryKey: ['enrichment-credits', workspaceId],
    queryFn: () => getEnrichmentCredits(workspaceId),
  });

  if (isNotLaunched(billingQuery.error) || isNotLaunched(usageQuery.error)) {
    // Members hold workspace seats whether or not Spec exists, so a server
    // saying Spec is unlaunched must not take that card with it. Returning bare
    // null here made this path disagree with the gate below about a card
    // neither of them owns.
    return <SeatsOnlyRow seatsCard={seatsCard} />;
  }

  const summary = billingQuery.data ?? null;
  const usage = usageQuery.data ?? null;
  // Either read says Free on its own, so a pending or failed billing read cannot offer Free a purchase.
  const free =
    summary?.eligibility === 'free_grant_only' || usage?.entitlementKind === 'free_monthly';

  return (
    <div className="space-y-6">
      {/*
        The allowance leads because it is what funds a paid workspace FIRST:
        credits are only reached once it runs out. It is also the one figure here with a
        meter, which is why it takes the full width and the stat row below stays
        a row of plain numbers.
      */}
      {usage ? (
        <SpecAllowanceHero summary={usage} billingHref={billingHref} onAddSeats={onManageSeats} />
      ) : usageQuery.error ? (
        <p className="text-sm text-muted-foreground">
          {specBillingSaveMessage(usageQuery.error, BILLING_FAILURE_MESSAGE)}
        </p>
      ) : (
        <SpecAllowanceHeroSkeleton />
      )}

      {enrichmentQuery.data ? (
        <EnrichmentCreditsPanel summary={enrichmentQuery.data} />
      ) : enrichmentQuery.error ? (
        <p className="text-sm text-muted-foreground" data-testid="enrichment-credits-unavailable">
          Enrichment credits are unavailable right now. Reload in a few minutes.
        </p>
      ) : (
        <SpecAllowanceHeroSkeleton />
      )}

      {/*
        One even row rather than cards of different widths appearing at
        different times. The workspace-seats card is passed in from the page
        because it is not a Spec concept and renders whether or not Spec is
        visible; putting it here keeps all three in one grid instead of leaving
        a lone card stranded above.
      */}
      <TooltipProvider delayDuration={200}>
        <div className={cn('grid gap-4', free ? 'sm:grid-cols-2' : 'sm:grid-cols-3')}>
          {seatsCard}
          {/*
            Three states per card, on its OWN read: a skeleton while it is still
            in flight, the figure when it lands, and a NAMED unavailable state
            when it fails. Rendering nothing on failure collapsed the row and
            left a reader unable to tell a missing capability from a broken one
            -- the same silence the Members card and the Tier column were fixed
            for. Free is the exception: AI seats are not part of that plan, so
            the card is absent rather than labeled as missing.
          */}
          {free ? null : summary ? (
            <UsageStatCard
              label="AI seats"
              value={
                summary.purchasedSeatQuantity === 0 && summary.assignedSeatCount === 0
                  ? 'None yet'
                  : seatPhrase(summary.assignedSeatCount, summary.purchasedSeatQuantity)
              }
              hint="AI seats assigned to members, of the AI seats this workspace has bought. An AI seat is what lets someone use Spec."
              icon={HardHat}
              action={
                onManageSeats
                  ? { label: 'Add seats', onClick: onManageSeats }
                  : { label: 'Add seats', to: billingHref }
              }
              detailsHref={membersHref}
            />
          ) : billingQuery.error ? (
            <UsageStatCardUnavailable label="AI seats" icon={HardHat} />
          ) : (
            <UsageStatCardSkeleton />
          )}
          {usage ? (
            <UsageStatCard
              testId="balance-card"
              label="Credit balance"
              value={formatSpecBalanceUsd(usage.purchasedRemaining)}
              // Free spends credits before its monthly grant; every paid plan spends them after.
              hint={
                free
                  ? 'Credits this workspace owns. Spent on AI chat before your free AI usage, and on agents, lookups, and enrichment past its monthly credits.'
                  : 'Credits this workspace owns. Spent on AI chat once your free trial or monthly AI usage runs out, and on agents, lookups, and enrichment past its monthly credits.'
              }
              icon={Coins}
              action={
                free
                  ? { label: 'Upgrade', to: billingHref }
                  : canBuyCredits
                    ? { label: 'Buy credits', onClick: () => setBuyOpen(true) }
                    : { label: 'Buy credits', to: billingHref }
              }
              detailsHref={billingHref}
            />
          ) : usageQuery.error ? (
            <UsageStatCardUnavailable label="Credit balance" icon={Coins} />
          ) : (
            <UsageStatCardSkeleton />
          )}
        </div>
      </TooltipProvider>

      <SpecRateLimits queryRows={queryRows} webCalls={webCalls} />

      <BuyCreditsDialog
        workspaceId={workspaceId}
        open={buyOpen}
        onOpenChange={setBuyOpen}
        writesPaused={writesPaused}
        allowanceName={usage?.entitlementKind === 'trial' ? 'free AI trial' : 'monthly AI usage'}
      />
    </div>
  );
}

/**
 * The guard. Owns exactly one hook (`useSpecLaunchState`) and returns null
 * ABOVE every hook `SpecUsageSectionContent` owns, so a mid-session launch
 * flip changes what renders, never how many hooks ran.
 */
function SpecUsageSection(props: {
  workspaceId: string;
  membersHref: string;
  billingHref: string;
  seatsCard: React.ReactNode;
  onManageSeats: (() => void) | null;
  canBuyCredits: boolean;
}) {
  const launchState = useSpecLaunchState();
  // Members are not a Spec concept, so that card still renders while Spec is
  // dark. Returning it from the guard rather than from the page keeps it in the
  // same grid in both states, instead of moving when Spec turns on.
  if (!isSpecSurfaceVisible(launchState)) {
    return <SeatsOnlyRow seatsCard={props.seatsCard} />;
  }
  return <SpecUsageSectionContent {...props} writesPaused={launchState !== 'on'} />;
}

/**
 * The page's shape before the workspace itself has loaded.
 *
 * Gate-aware on purpose: it reads the same launch state `SpecUsageSection`
 * does, so a workspace with Spec dark is not shown three cards settling into
 * one. The launch store is synchronous, so this costs no read.
 */
function UsageSkeleton() {
  const launchState = useSpecLaunchState();
  const specVisible = isSpecSurfaceVisible(launchState);

  return (
    <div className="space-y-6">
      {specVisible ? <SpecAllowanceHeroSkeleton /> : null}
      <div className="grid gap-4 sm:grid-cols-3">
        <UsageStatCardSkeleton />
        {specVisible ? (
          <>
            <UsageStatCardSkeleton />
            <UsageStatCardSkeleton />
          </>
        ) : null}
      </div>
    </div>
  );
}

export function UsageSettingsPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const [workspace, setWorkspace] = React.useState<WorkspaceSummary | null>(null);
  const [seats, setSeats] = React.useState<WorkspaceMembersResponse['seats'] | null>(null);
  // "Not loaded yet" and "loaded with nothing to show" differ: a failed read must not pulse forever.
  const [seatsSettled, setSeatsSettled] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [seatsOpen, setSeatsOpen] = React.useState(false);
  const requestRef = React.useRef(0);
  const queryClient = useQueryClient();
  const specVisible = isSpecSurfaceVisible(useSpecLaunchState());

  React.useEffect(() => {
    let active = true;
    const requestId = ++requestRef.current;
    setWorkspace(null);
    setSeats(null);
    setSeatsSettled(false);
    setLoadError(null);

    async function load() {
      if (!workspaceSlug) {
        setLoadError('Select a workspace before viewing usage.');
        return;
      }

      let loadedWorkspace: WorkspaceSummary;
      try {
        loadedWorkspace = await getWorkspace(workspaceSlug);
      } catch (err) {
        if (active && requestRef.current === requestId) {
          setLoadError(err instanceof Error ? err.message : 'Unable to load this workspace.');
          setSeatsSettled(true);
        }
        return;
      }
      if (!active || requestRef.current !== requestId) return;
      setWorkspace(loadedWorkspace);

      void listWorkspaceMembers(loadedWorkspace.slug)
        .then((result) => {
          if (active && requestRef.current === requestId) setSeats(result.seats);
        })
        .catch(() => {}) // the card says so rather than pulsing forever
        .finally(() => {
          if (active && requestRef.current === requestId) setSeatsSettled(true);
        });
    }

    void load();
    return () => {
      active = false;
    };
  }, [workspaceSlug]);

  const membersHref = getWorkspaceScopedPath(pathname, '/settings/members');
  const billingHref = getWorkspaceScopedPath(pathname, '/settings/billing');

  // The same gate Billing uses for its Manage seats button; anyone else keeps the links.
  const canManageWorkspaceSeats = seats?.canManage ?? false;
  const canManageAiSeats =
    specVisible &&
    (workspace?.role === 'owner' || workspace?.role === 'admin') &&
    (workspace?.effectivePlan ?? 'free') !== 'free';
  const openSeats =
    seats && (canManageWorkspaceSeats || canManageAiSeats) ? () => setSeatsOpen(true) : null;

  const refreshAfterSeats = async () => {
    if (!workspace) return;
    void queryClient.invalidateQueries({ queryKey: ['spec-billing', workspace.id] });
    void queryClient.invalidateQueries({ queryKey: ['spec-usage-summary', workspace.id] });
    try {
      setSeats((await listWorkspaceMembers(workspace.slug)).seats);
    } catch {
      // A failed refresh leaves the prior figures; the next load reconciles.
    }
  };

  return (
    <SettingsShell>
      <SettingsPageHeader
        eyebrow="Workspace"
        title="Usage"
        description="What this workspace has consumed this period."
      />

      {loadError ? <p className="mb-6 text-sm text-destructive">{loadError}</p> : null}

      {loadError ? null : workspace ? (
        <SpecUsageSection
          workspaceId={workspace.id}
          membersHref={membersHref}
          billingHref={billingHref}
          onManageSeats={openSeats}
          canBuyCredits={workspace.role === 'owner' || workspace.role === 'admin'}
          seatsCard={
            !seatsSettled ? (
              <UsageStatCardSkeleton />
            ) : seats ? (
              <UsageStatCard
                label="Members"
                value={seatPhrase(seats.used, seats.limit)}
                hint="Members holding a workspace seat, of the seats this workspace has bought. Everyone who signs in needs one."
                icon={Users}
                action={
                  // Starter can buy AI seats but not workspace seats, so the dialog would have nothing to add here.
                  openSeats && canManageWorkspaceSeats
                    ? { label: 'Add members', onClick: openSeats }
                    : { label: 'Add members', to: membersHref }
                }
                detailsHref={membersHref}
              />
            ) : (
              // Settled with nothing to show. A skeleton here would claim the
              // number is still coming, which is how the old catch left this
              // card pulsing for the rest of the session.
              <UsageStatCardUnavailable label="Members" icon={Users} />
            )
          }
        />
      ) : (
        <UsageSkeleton />
      )}

      {workspace && seats && openSeats ? (
        <ManageSeatsDialog
          open={seatsOpen}
          onOpenChange={setSeatsOpen}
          workspaceId={workspace.id}
          includedSeats={seats.limit - seats.quantity}
          currentQuantity={seats.quantity}
          onCommitted={refreshAfterSeats}
          canManageWorkspaceSeats={canManageWorkspaceSeats}
        />
      ) : null}
    </SettingsShell>
  );
}
